/**
 * Redirect State Management Service
 *
 * Manages the "redirect after login" flow using localStorage exclusively.
 * This prevents open redirect attacks that could occur with URL parameters.
 *
 * This service ensures users reach their intended destination after
 * completing authentication and onboarding flows. All writes route through
 * `safeLocalStorage` (the central guarded setter) so the invite-link token
 * tripwire can never be bypassed — a saved redirect must never carry a token.
 */
import { logger } from '@/lib/logger'
import { safeLocalStorage } from '@/services/safeLocalStorage'

const STORAGE_KEY = 'nopressure:redirectAfterLogin'

/**
 * Extracts workspace ID from a path like /workspaces/123/... (also supports legacy /organizations/123/...)
 * @param path - The path to extract from
 * @returns workspace ID or null if not found
 */
export function extractOrgIdFromPath(path: string): string | null {
  // Character class terminates at /, ?, or # so callers can pass a full URL
  // path with query and/or hash (e.g. router location pathname+search+hash)
  // without the workspace ID absorbing the suffix.
  const match = path.match(/^\/(?:workspaces|organizations)\/([^/?#]+)/)
  return match?.[1] ?? null
}

/**
 * Validates that a redirect path is safe (internal only, prevents open redirect attacks)
 * @param path - The path to validate
 * @returns true if path is a safe internal path
 *
 * Exported because the invite-link deep-link flow re-asserts a built `dest`
 * against the same denylist before persisting it (plan §5.3).
 */
export function isValidRedirect(path: string): boolean {
  // Must not be empty
  if (path.length === 0) return false

  // Must start with / (internal path)
  if (!path.startsWith('/')) return false

  // Must NOT start with // (protocol-relative URL - security risk)
  if (path.startsWith('//')) return false

  // Must not contain dangerous characters or protocols
  const dangerousPatterns = ['javascript:', 'data:', 'vbscript:', '<script', 'onerror=', 'onclick=']

  const lowerPath = path.toLowerCase()
  for (const pattern of dangerousPatterns) {
    if (lowerPath.includes(pattern)) return false
  }

  return true
}

/**
 * Gets redirect path from localStorage
 */
function getFromStorage(): string | null {
  const stored = safeLocalStorage.get(STORAGE_KEY)
  if (stored && isValidRedirect(stored)) {
    return stored
  }

  return null
}

/**
 * Redirect State Service
 *
 * Public API for managing redirect state through authentication flows
 */
export const persistedRedirectUrl = {
  /**
   * Saves the current path as the redirect destination
   * Stores in localStorage for persistence across OAuth redirects
   *
   * @param path - The path to redirect to after authentication
   */
  save(path: string): void {
    if (!isValidRedirect(path)) {
      logger.warn('Invalid redirect path, not saving:', path)
      return
    }

    safeLocalStorage.set(STORAGE_KEY, path)
  },

  /**
   * Gets the saved redirect destination from localStorage
   *
   * @returns The redirect path, or null if none saved
   */
  get(): string | null {
    return getFromStorage()
  },

  /**
   * Clears the saved redirect destination
   * Should be called after successfully redirecting to the intended destination
   */
  clear(): void {
    safeLocalStorage.remove(STORAGE_KEY)
  },
}
